import React, { useState, useEffect } from "react";
import { Navigation } from "../components/Navigation";
import { COPY } from "../constants/copy";

interface HeroSectionProps {
  onNext: () => void;
  onPrevious: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  onNext,
  onPrevious,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="w-full min-h-screen flex flex-col items-center justify-center px-4 bg-warmWhite dark:bg-darkBg transition-colors duration-400 py-20 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Large gradient orbs */}
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-heartRed to-softRose opacity-10 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-blushPink to-heartRed opacity-10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>

        {/* Floating hearts background (subtle) */}
        <div
          className="absolute top-20 left-10 text-6xl text-heartRed opacity-5 animate-float"
          style={{ animationDuration: "6s" }}
        >
          ❤️
        </div>
        <div
          className="absolute bottom-32 right-12 text-5xl text-softRose opacity-5 animate-float"
          style={{ animationDuration: "8s", animationDelay: "1s" }}
        >
          ❤️
        </div>
        <div
          className="absolute top-1/2 right-1/4 text-4xl text-blushPink opacity-5 animate-float"
          style={{ animationDuration: "7s", animationDelay: "2s" }}
        >
          ❤️
        </div>
      </div>

      <div className="max-w-3xl w-full space-y-6 relative z-10 flex flex-col items-center justify-center">
        {/* Hero Banner */}
        <div
          className={`transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          } w-full`}
        >
          <div className="relative">
            {/* Gradient background banner */}
            <div className="absolute -inset-4 bg-gradient-to-r from-heartRed via-blushPink to-softRose rounded-3xl opacity-20 blur-2xl"></div>

            {/* Main banner content */}
            <div className="relative bg-gradient-to-br from-blushPink/40 to-softRose/40 dark:from-darkCard/60 dark:to-gray-700/60 backdrop-blur-md rounded-3xl p-12 md:p-16 border border-softRose/50 dark:border-gray-600/50 shadow-2xl">
              {/* Decorative top ornaments */}
              <div className="flex justify-center gap-4 mb-8 text-3xl text-heartRed dark:text-softRose opacity-60">
                <span className="animate-pulse">✦</span>
                <span
                  className="animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                >
                  ❤️
                </span>
                <span className="animate-pulse">✦</span>
              </div>

              {/* Large "60" with glow effect */}
              <div className="mb-8">
                <div className="relative inline-block w-full">
                  {/* Glow background */}
                  <div className="absolute inset-0 bg-gradient-to-b from-heartRed to-softRose opacity-30 blur-3xl rounded-full"></div>

                  {/* Number */}
                  <div className="relative text-9xl md:text-10xl font-light text-heartRed dark:text-softRose text-center tracking-tighter drop-shadow-lg">
                    60
                  </div>
                </div>
              </div>

              {/* Heading */}
              <h1 className="text-4xl md:text-5xl font-light text-deepCharcoal dark:text-darkText text-center leading-tight mb-6">
                {COPY.hero.heading}
              </h1>

              {/* Decorative divider with gradient */}
              <div className="flex items-center justify-center gap-4 my-8">
                <div className="h-0.5 w-16 bg-gradient-to-r from-transparent to-heartRed dark:to-softRose"></div>
                <div className="flex gap-1 text-heartRed dark:text-softRose">
                  <span className="text-lg">✦</span>
                  <span className="text-lg opacity-50">✦</span>
                </div>
                <div className="h-0.5 w-16 bg-gradient-to-l from-transparent to-heartRed dark:to-softRose"></div>
              </div>

              {/* Subtext with enhanced styling */}
              <p className="text-base md:text-lg text-deepCharcoal dark:text-gray-200 text-center whitespace-pre-line leading-relaxed font-light mb-8">
                {COPY.hero.subtext}
              </p>

              {/* Decorative bottom ornaments */}
              <div className="flex justify-center gap-3 text-2xl text-softRose dark:text-blushPink opacity-40 mt-8">
                <span>✦</span>
                <span>✦</span>
                <span>✦</span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div
          className={`flex justify-center transition-all duration-1000 ${
            isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
          }`}
          style={{ transitionDelay: "0.5s" }}
        >
          <Navigation
            onNext={onNext}
            onPrevious={onPrevious}
            nextLabel={COPY.hero.button}
            showPrevious={true}
          />
        </div>

        {/* Bottom accent line */}
        <div className="flex justify-center">
          <div className="h-px w-24 bg-gradient-to-r from-transparent via-heartRed to-transparent dark:via-softRose opacity-40"></div>
        </div>
      </div>

      {/* CSS for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-30px);
          }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};
